import { AssetAmount } from "@sundaeswap/asset";
import { Fraction, TFractionLike } from "@sundaeswap/fraction";
import {
  MAX_PRICE_IMPACT_CHECKBOX_CONFIRMATION,
  MAX_PRICE_IMPACT_SUCCESS,
  MAX_PRICE_IMPACT_WARNING,
  MAX_SLIPPAGE_TOLERANCE,
  MIN_SLIPPAGE_TOLERANCE,
} from "../constants/exchange.constants";
import { BASE_BUY_ASSET_IDS } from "../constants/SundaeSwap.constants";
import { IAssetMetaData } from "../types/Asset.types";
import { multiplyBy, numberI18n } from "./number-format";

export type TGetExchangeValue = {
  inputReserve: bigint;
  lpFee: TFractionLike;
  outputReserve: bigint;
  value: bigint;
};

/**
 * Returns boolean whether there's insufficient quantity of an asset.
 */
export const isSufficientQuantity = ({
  quantity,
  value,
}: {
  quantity?: number;
  value: string | number;
}) => {
  if (!quantity) return false;

  const parsedValue = typeof value === "string" ? Number(value) : value;

  return quantity >= parsedValue;
};

/**
 * Based on the actual price impact, we wil return states in our UI that can be passed to any `<Text />` based component.
 */
export const getPriceImpactStatus = (priceImpact?: number) => {
  if (!priceImpact) return;

  if (multiplyBy(priceImpact, 100) <= MAX_PRICE_IMPACT_SUCCESS) {
    return "success";
  }

  if (multiplyBy(priceImpact, 100) <= MAX_PRICE_IMPACT_WARNING) {
    return "warning";
  }

  return "error";
};

/**
 * Determines whether to display a checkbox confirmation based on the price impact.
 * Returns true if the price impact exceeds the predefined maximum threshold.
 *
 * @param {Fraction} [priceImpact] - The price impact to evaluate.
 * @return {boolean} - True if checkbox confirmation should be displayed, false otherwise.
 */
export const getShowPriceImpactWarning = (priceImpact?: Fraction) => {
  return Boolean(priceImpact?.greaterThan(MAX_PRICE_IMPACT_CHECKBOX_CONFIRMATION));
};

/**
 * Transforms the `slippageTolerance` to a proper i18n string.
 */
export const handleSlippageToleranceI18N = (slippageTolerance: number | string) =>
  Number(
    numberI18n(Number(slippageTolerance), {
      maximumFractionDigits: 2,
    })
  );

/**
 * This function provides the proper `slippageTolerance` value for the Input inside the `ExchangeSettingsDialog` component.
 */
export const handleSlippageValueOnBlur = (slippageTolerance: number) => {
  if (slippageTolerance > MAX_SLIPPAGE_TOLERANCE || slippageTolerance < MIN_SLIPPAGE_TOLERANCE) {
    return handleSlippageToleranceI18N(
      Math.max(
        MIN_SLIPPAGE_TOLERANCE,
        Math.min(MAX_SLIPPAGE_TOLERANCE, slippageTolerance || MIN_SLIPPAGE_TOLERANCE)
      )
    );
  }

  return slippageTolerance;
};

/** Returns the current Exchange context. Either "Buy" (when `given` is included in the `BASE_BUY_ASSET_IDS`) or the classic "Swap" */
export const getExchangeDirectionalContext = (given?: AssetAmount<IAssetMetaData>) => {
  if (!given) return "swap";
  const metadata = given.metadata;

  if (
    BASE_BUY_ASSET_IDS.includes(metadata.assetId) ||
    BASE_BUY_ASSET_IDS.includes(metadata.policyId)
  ) {
    return "buy";
  }

  return "swap";
};
